import React, { useState, useCallback, useEffect, useRef } from 'react';
import { mergeClasses, Skeleton, SkeletonItem } from '@fluentui/react-components';
import {
  People20Regular,
  Location16Regular,
  ChevronRight12Regular,
  CheckmarkRegular,
  ErrorCircle16Filled,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { SpaceStatusBadge } from '../SpaceStatusBadge/SpaceStatusBadge';
import { PillBadge } from '@components/common/PillBadge';
import { RoomPreferenceCard } from '../RoomPreferenceCard/RoomPreferenceCard';
import { BasicButton } from '@components/common/BasicButton';
import { useSpaceCardStyles } from './SpaceCard.styles';
import { getRoomLayoutMeta } from './SpaceCard.constants';
import placeholderImage from '@/assets/images/space-card-room-placeholder.png';
import { useAuthenticatedImage } from '@/hooks/useAuthenticatedImage';
import { SpaceImageFallback } from '@components/spaces/SpaceImageFallback';
import { isSpaceBookable } from '@/utils/spaceAvailability';
import type { SpaceWithAvailability } from '@store/models';

export interface SpaceCardProps {
  space: SpaceWithAvailability;
  selected?: boolean;
  attendeeCount?: number;
  selectedSetups?: string[];
  isRecommended?: boolean;
  onSelect: (spaceId: string) => void;
  onSetupChange?: (spaceId: string, setupCodes: string[]) => void;
  className?: string;
  /**
   * Opt-in to the Figma 2026 visual style (white surface, neutral border,
   * 8 px radius, navy accent on selected). Defaults to `false` so Planner /
   * Admin call sites render the legacy look unchanged. Combined with
   * `isRecommended` (already present, presentational-only per FR-014a) to
   * show the warm-tinted Recommended badge on the first list item.
   */
  figmaVariant?: boolean;
}

export const SpaceCard: React.FC<SpaceCardProps> = ({
  space,
  selected = false,
  attendeeCount,
  selectedSetups = [],
  isRecommended = false,
  onSelect,
  onSetupChange,
  className,
  figmaVariant = false,
}) => {
  const { t } = useTranslation('spaceCard');
  const styles = useSpaceCardStyles();
  // A space is bookable when either:
  //   1. The API returned no per-day `spaceAvailability` info (unknown → assume yes), OR
  //   2. At least one day in `spaceAvailability` is available.
  // See `isSpaceBookable` for the single source of truth.
  const isBooked = !isSpaceBookable(space);
  const hasRoomPreferences = space.supportedRoomLayouts.length > 0 || space.isBoardRoom;

  const [isExpanded, setIsExpanded] = useState(selected && hasRoomPreferences);
  const expandRef = useRef<HTMLDivElement>(null);
  const {
    src: imgSrc,
    isLoading: isImageLoading,
    isError: isImageError,
  } = useAuthenticatedImage(space.imageUrl, placeholderImage);
  const showFallback = !space.imageUrl || isImageError;
  const [internalSetups, setInternalSetups] = useState<string[]>(selectedSetups);

  // Auto-expand room preferences when card becomes selected
  useEffect(() => {
    if (selected && hasRoomPreferences) {
      setIsExpanded(true);
    } else if (!selected) {
      setIsExpanded(false);
    }
  }, [selected, hasRoomPreferences]);

  // When the room-setup preferences expand, scroll the panel into view so the
  // newly revealed arrangement styles aren't left below the fold (or hidden
  // behind the pinned action bar). Wait for the max-height expand animation
  // (0.3s) to finish so the panel has its full height before scrolling.
  useEffect(() => {
    if (!isExpanded) return;
    const node = expandRef.current;
    if (!node) return;
    const timer = window.setTimeout(() => {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 320);
    return () => window.clearTimeout(timer);
  }, [isExpanded]);

  // Keep internal setup state in sync with parent-provided selections, including
  // resets to an empty list when the parent clears the selection (e.g. switching
  // to a different space card). Compare by content to avoid update loops caused
  // by a new array reference on each render.
  const selectedSetupsKey = selectedSetups.join('|');
  useEffect(() => {
    setInternalSetups((prev) => {
      if (prev.join('|') === selectedSetupsKey) return prev;
      return selectedSetupsKey ? selectedSetupsKey.split('|') : [];
    });
  }, [selectedSetupsKey]);

  const handleToggleExpand = useCallback(() => {
    if (!isBooked) {
      setIsExpanded((prev) => !prev);
    }
  }, [isBooked]);

  const handleToggleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isBooked) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsExpanded((prev) => !prev);
      }
    },
    [isBooked]
  );

  const handleSelectClick = useCallback(() => {
    if (!isBooked) {
      onSelect(space.spaceId);
    }
  }, [isBooked, onSelect, space.spaceId]);

  const handlePreferenceChange = useCallback(
    (code: string, checked: boolean) => {
      // For spaces with only a single supported layout (or a boardroom's fixed
      // layout), the arrangement is locked once selected — silently ignore any
      // attempt to uncheck it.
      const isSingleLayout = space.isBoardRoom || space.supportedRoomLayouts.length === 1;
      if (isSingleLayout && !checked) {
        return;
      }
      const next = checked
        ? internalSetups.includes(code)
          ? internalSetups
          : [...internalSetups, code]
        : internalSetups.filter((c) => c !== code);
      setInternalSetups(next);
      onSetupChange?.(space.spaceId, next);
    },
    [
      onSetupChange,
      internalSetups,
      space.spaceId,
      space.isBoardRoom,
      space.supportedRoomLayouts.length,
    ]
  );

  const selectedCapacitySum = space.supportedRoomLayouts
    .filter((layout) => internalSetups.includes(layout.code))
    .reduce((sum, layout) => sum + layout.capacity, 0);

  const showCapacityWarning =
    internalSetups.length > 0 &&
    space.roomCapacity !== undefined &&
    selectedCapacitySum > space.roomCapacity;

  // Seats provided by the chosen arrangement; boardrooms seat the full room.
  const selectedSetupCapacity = space.isBoardRoom ? space.roomCapacity : selectedCapacitySum;

  // Warn when the attendee count exceeds what the selected room setup can seat.
  const showAttendeeCapacityWarning =
    selected &&
    attendeeCount !== undefined &&
    (hasRoomPreferences
      ? space.isBoardRoom
        ? selectedSetupCapacity !== undefined && attendeeCount > selectedSetupCapacity
        : internalSetups.length > 0 && attendeeCount > selectedCapacitySum
      : space.roomCapacity !== undefined && attendeeCount > space.roomCapacity);

  const locationParts = [
    space.floor ? `Floor ${space.floor}` : '',
    space.roomNumber ? `Room ${space.roomNumber}` : '',
  ].filter(Boolean);

  return (
    <article
      className={mergeClasses(
        styles.root,
        figmaVariant && styles.rootFigma,
        selected && styles.selected,
        figmaVariant && selected && styles.selectedFigma,
        className
      )}
      aria-label={space.spaceName}
    >
      {/* Top Row: Image + Content side by side */}
      <div className={styles.topRow}>
        {/* Image Section Wrapper — badges sit here (not clipped) */}
        <div className={styles.imageSectionWrapper}>
          <div className={styles.imageSection} data-testid="image-section">
            {isImageLoading ? (
              <Skeleton aria-label="Loading image">
                <SkeletonItem className={styles.image} />
              </Skeleton>
            ) : showFallback ? (
              <SpaceImageFallback className={styles.image} />
            ) : (
              <img src={imgSrc} alt={space.spaceName} className={styles.image} />
            )}
            {!showFallback && !isImageLoading && (
              <div className={styles.gradientOverlay} data-testid="gradient-overlay" />
            )}
            {isRecommended && (
              <div className={styles.badgeContainer}>
                <SpaceStatusBadge status="recommended" />
              </div>
            )}
          </div>
          {!isBooked && (
            <div className={styles.statusBadge} aria-label={t('spaceCard.available')}>
              <SpaceStatusBadge
                status="available"
                spaceAvailability={space.spaceAvailability}
                totalDays={space.spaceAvailability?.length}
              />
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className={styles.contentSection}>
          <div className={styles.name} title={space.spaceName}>
            {space.spaceName}
          </div>

          <div className={styles.metadataRow}>
            <span className={styles.metadataItem}>
              <People20Regular aria-hidden="true" />
              {space.roomCapacity !== undefined
                ? t('spaceCard.capacityLabel', { count: space.roomCapacity })
                : '\u2014'}
            </span>
            <span className={styles.metadataSeparator} aria-hidden="true">
              |
            </span>
            <span className={styles.metadataItem}>
              <Location16Regular aria-hidden="true" />
              {locationParts.join(', ')}
            </span>
          </div>

          {(space.amenityCodes ?? []).length > 0 && (
            <div className={styles.amenityRow} data-testid="amenity-row">
              {(space.amenityCodes ?? []).map((code) => (
                <PillBadge key={code} amenityCode={code} available={!isBooked} />
              ))}
            </div>
          )}

          {hasRoomPreferences && (
            <div className={styles.buttonArea}>
              <div
                role="button"
                tabIndex={isBooked ? -1 : 0}
                aria-expanded={isExpanded}
                onClick={handleToggleExpand}
                onKeyDown={handleToggleKeyDown}
                className={mergeClasses(
                  styles.collapsibleHeader,
                  isBooked && styles.collapsibleHeaderDisabled
                )}
                data-testid="room-preference-toggle"
              >
                <span
                  className={mergeClasses(styles.chevronIcon, isExpanded && styles.chevronExpanded)}
                  aria-hidden="true"
                >
                  <ChevronRight12Regular />
                </span>
                {t('spaceCard.roomSetupPreference')}
              </div>

              <BasicButton
                variant="primary"
                className={styles.selectButton}
                disabled={isBooked}
                onClick={handleSelectClick}
                icon={selected ? <CheckmarkRegular /> : null}
                data-testid="select-button"
              >
                {selected ? t('spaceCard.selected') : t('spaceCard.selectThisSpace')}
              </BasicButton>
            </div>
          )}

          {!hasRoomPreferences && (
            <div className={styles.buttonArea}>
              <BasicButton
                variant="primary"
                className={styles.selectButton}
                disabled={isBooked}
                onClick={handleSelectClick}
                icon={selected ? <CheckmarkRegular /> : null}
                data-testid="select-button"
              >
                {selected ? t('spaceCard.selected') : t('spaceCard.selectThisSpace')}
              </BasicButton>
            </div>
          )}
        </div>
      </div>

      {/* Expanded Area: Full width below the card with animation */}
      {hasRoomPreferences && (
        <div
          ref={expandRef}
          className={mergeClasses(styles.expandContainer, isExpanded && styles.expandContainerOpen)}
        >
          <div className={styles.expandedArea} data-testid="expanded-area">
            <div className={styles.instructionText}>{t('spaceCard.selectRoomPreference')}</div>
            <div className={styles.preferencesGrid}>
              {space.isBoardRoom ? (
                <RoomPreferenceCard
                  key="boardroom"
                  title={getRoomLayoutMeta('boardroom').title}
                  description={getRoomLayoutMeta('boardroom').description}
                  maxCapacity={space.roomCapacity}
                  checked={internalSetups.includes('boardroom')}
                  disabled={isBooked}
                  onChange={(checked) => handlePreferenceChange('boardroom', checked)}
                />
              ) : (
                space.supportedRoomLayouts.map((layout) => {
                  const meta = getRoomLayoutMeta(layout.code);
                  return (
                    <RoomPreferenceCard
                      key={layout.code}
                      title={meta.title}
                      description={meta.description}
                      maxCapacity={layout.capacity}
                      checked={internalSetups.includes(layout.code)}
                      disabled={isBooked}
                      onChange={(checked) => handlePreferenceChange(layout.code, checked)}
                    />
                  );
                })
              )}
            </div>
            {showCapacityWarning && (
              <div className={styles.warningBanner} data-testid="capacity-warning">
                <span className={styles.warningIcon} aria-hidden="true">
                  <ErrorCircle16Filled />
                </span>
                <span>
                  <strong>{t('spaceCard.capacityWarningPrefix')}</strong>{' '}
                  {t('spaceCard.capacityWarningMessage')}
                </span>
              </div>
            )}
            {selected && internalSetups.length === 0 && (
              <div
                className={styles.warningBanner}
                data-testid="arrangement-required-warning"
                role="alert"
              >
                <span className={styles.warningIcon} aria-hidden="true">
                  <ErrorCircle16Filled />
                </span>
                <span>{t('spaceCard.arrangementRequired')}</span>
              </div>
            )}
            {showAttendeeCapacityWarning && (
              <div
                className={styles.warningBanner}
                data-testid="attendee-capacity-warning"
                role="alert"
              >
                <span className={styles.warningIcon} aria-hidden="true">
                  <ErrorCircle16Filled />
                </span>
                <span>{t('spaceCard.attendeeCapacityInsufficient')}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {!hasRoomPreferences && showAttendeeCapacityWarning && (
        <div className={styles.warningBanner} data-testid="attendee-capacity-warning" role="alert">
          <span className={styles.warningIcon} aria-hidden="true">
            <ErrorCircle16Filled />
          </span>
          <span>{t('spaceCard.attendeeCapacityInsufficient')}</span>
        </div>
      )}
    </article>
  );
};

SpaceCard.displayName = 'SpaceCard';
